import { useState } from "react";
import AdminLayout from "./components/AdminLayout";
import { useCMSSettings } from "@/hooks/useCMSSettings";

const tabs = [
  { id: "general", label: "عام", icon: "ri-settings-3-line" },
  { id: "seo", label: "SEO", icon: "ri-search-line" },
  { id: "pixel", label: "Pixel & Analytics", icon: "ri-bar-chart-box-line" },
  { id: "ai", label: "الذكاء الاصطناعي", icon: "ri-brain-line" },
  { id: "security", label: "الحماية و Cloudflare", icon: "ri-shield-check-line" },
  { id: "chatbot", label: "الشات بوت", icon: "ri-robot-2-line" },
  { id: "appearance", label: "المظهر والتواصل", icon: "ri-palette-line" },
  { id: "terms", label: "الشروط والخصوصية", icon: "ri-file-list-3-line" },
];

export default function AdminSettings() {
  const [activeTab, setActiveTab] = useState("general");
  const [legalSubTab, setLegalSubTab] = useState<"terms" | "privacy">("terms");

  const {
    info,
    hours,
    social,
    emergency,
    seo,
    pixels,
    security,
    saveStatus,
    hasChanges,
    updateInfo,
    updateHours,
    updateSocial,
    updateEmergency,
    updateSeo,
    updatePixels,
    updateSecurity,
    save,
    reset,
  } = useCMSSettings();

  const handleUpdateTermsSection = (index: number, field: string, val: string) => {
    const current = [...(info.termsSections || [])];
    current[index] = { ...current[index], [field]: val };
    updateInfo({ ...info, termsSections: current });
  };

  const handleAddTermsSection = () => {
    const current = [...(info.termsSections || [])];
    current.push({
      title: `بند جديد ${current.length + 1}`,
      title_en: `New Section ${current.length + 1}`,
      content: "",
      content_en: "",
    });
    updateInfo({ ...info, termsSections: current });
  };

  const handleRemoveTermsSection = (index: number) => {
    const current = [...(info.termsSections || [])];
    current.splice(index, 1);
    updateInfo({ ...info, termsSections: current });
  };

  const handleUpdatePrivacySection = (index: number, field: string, val: string) => {
    const current = [...(info.privacySections || [])];
    current[index] = { ...current[index], [field]: val };
    updateInfo({ ...info, privacySections: current });
  };

  const handleAddPrivacySection = () => {
    const current = [...(info.privacySections || [])];
    current.push({
      title: `بند جديد ${current.length + 1}`,
      title_en: `New Section ${current.length + 1}`,
      content: "",
      content_en: "",
    });
    updateInfo({ ...info, privacySections: current });
  };

  const handleRemovePrivacySection = (index: number) => {
    const current = [...(info.privacySections || [])];
    current.splice(index, 1);
    updateInfo({ ...info, privacySections: current });
  };

  const handleSave = async () => {
    await save();
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-800">الإعدادات</h2>
            <p className="text-sm text-gray-500 mt-1">إدارة إعدادات الموقع واللوحة بشكل ديناميكي</p>
          </div>
          <div className="flex items-center gap-3">
            {saveStatus === "saved" && (
              <span className="text-sm text-emerald-600 flex items-center gap-1">
                <i className="ri-check-line font-bold" />
                تم الحفظ بنجاح
              </span>
            )}
            {saveStatus === "saving" && (
              <span className="text-sm text-gray-500 flex items-center gap-1">
                <i className="ri-loader-4-line animate-spin" />
                جاري الحفظ...
              </span>
            )}
            {saveStatus === "error" && (
              <span className="text-sm text-red-600 flex items-center gap-1">
                <i className="ri-error-warning-line" />
                فشل في حفظ التعديلات
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Settings Tabs */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full text-right px-4 py-3 flex items-center gap-3 transition-colors border-b border-gray-50 last:border-0 cursor-pointer ${
                    activeTab === tab.id
                      ? "bg-[#2E4E45]/5 text-[#2E4E45] border-r-2 border-r-[#2E4E45]"
                      : "text-gray-500 hover:bg-gray-50/50"
                  }`}
                >
                  <i className={`${tab.icon} text-base`} />
                  <span className="text-sm font-medium">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Settings Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
              {activeTab === "general" && (
                <div className="space-y-6">
                  <h3 className="text-base font-bold text-gray-800 mb-4">إعدادات عامة</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">اسم العيادة</label>
                      <input
                        type="text"
                        value={info.name || ""}
                        onChange={(e) => updateInfo({ ...info, name: e.target.value })}
                        className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#2E4E45] focus:ring-2 focus:ring-[#2E4E45]/10"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">رقم الهاتف</label>
                      <input
                        type="text"
                        value={info.phone || ""}
                        onChange={(e) => updateInfo({ ...info, phone: e.target.value })}
                        className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#2E4E45] focus:ring-2 focus:ring-[#2E4E45]/10"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">البريد الإلكتروني</label>
                      <input
                        type="email"
                        value={info.email || ""}
                        onChange={(e) => updateInfo({ ...info, email: e.target.value })}
                        className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#2E4E45] focus:ring-2 focus:ring-[#2E4E45]/10"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">رقم WhatsApp للطوارئ</label>
                      <input
                        type="text"
                        value={emergency.text || ""}
                        onChange={(e) => updateEmergency({ ...emergency, text: e.target.value })}
                        className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#2E4E45] focus:ring-2 focus:ring-[#2E4E45]/10"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">العنوان</label>
                    <textarea
                      rows={2}
                      value={info.address || ""}
                      onChange={(e) => updateInfo({ ...info, address: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#2E4E45] focus:ring-2 focus:ring-[#2E4E45]/10 resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">ساعات العمل</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">السبت - الخميس</label>
                        <input
                          type="text"
                          value={hours.regular || ""}
                          onChange={(e) => updateHours({ ...hours, regular: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#2E4E45]"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">الجمعة</label>
                        <input
                          type="text"
                          value={hours.weekend || ""}
                          onChange={(e) => updateHours({ ...hours, weekend: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#2E4E45]"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "seo" && (
                <div className="space-y-6">
                  <h3 className="text-base font-bold text-gray-800 mb-4">إعدادات SEO</h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">عنوان الموقع (Title)</label>
                    <input
                      type="text"
                      value={seo?.title || ""}
                      onChange={(e) => updateSeo({ ...seo, title: e.target.value })}
                      placeholder="عيادة الشفاء الطبية | رعاية صحية متكاملة"
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#2E4E45] focus:ring-2 focus:ring-[#2E4E45]/10"
                    />
                    <p className="text-xs text-gray-400 mt-1">يظهر في نتائج البحث وعلى المتصفح</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">وصف الموقع (Meta Description)</label>
                    <textarea
                      rows={3}
                      value={seo?.description || ""}
                      onChange={(e) => updateSeo({ ...seo, description: e.target.value })}
                      placeholder="عيادة الشفاء تقدم خدمات طبية متقدمة تشمل الجلدية، التجميل، العلاج الطبيعي والاستشارات العامة. احجز موعدك الآن."
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#2E4E45] focus:ring-2 focus:ring-[#2E4E45]/10 resize-none"
                    />
                    <p className="text-xs text-gray-400 mt-1">150-160 حرف للحصول على أفضل نتيجة</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">الكلمات المفتاحية (Keywords)</label>
                    <input
                      type="text"
                      value={seo?.keywords || ""}
                      onChange={(e) => updateSeo({ ...seo, keywords: e.target.value })}
                      placeholder="عيادة طبية، حجز موعد، طبيب جلدية، علاج بالخلايا، الرياض"
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#2E4E45] focus:ring-2 focus:ring-[#2E4E45]/10"
                    />
                  </div>
                </div>
              )}

              {activeTab === "pixel" && (
                <div className="space-y-6">
                  <h3 className="text-base font-bold text-gray-800 mb-4">Pixel & Analytics</h3>
                  <p className="text-xs text-gray-400">إعداد أكواد التتبع والتحليلات الخاصة بالحملات التسويقية.</p>

                  {[
                    { name: "Facebook Pixel", id: "fb_pixel", placeholder: "123456789012345" },
                    { name: "Google Analytics (GA4)", id: "ga4", placeholder: "G-XXXXXXXXXX" },
                    { name: "Google Tag Manager", id: "gtm", placeholder: "GTM-XXXXXX" },
                  ].map((pixel) => (
                    <div key={pixel.id} className="flex items-center gap-4 p-4 rounded-lg border border-gray-100">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">{pixel.name}</label>
                        <input
                          type="text"
                          value={pixels?.[pixel.id]?.id || ""}
                          onChange={(e) => updatePixels({ 
                            ...pixels, 
                            [pixel.id]: { ...pixels[pixel.id], id: e.target.value } 
                          })}
                          placeholder={pixel.placeholder}
                          className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#2E4E45]"
                        />
                      </div>
                      <div className="flex items-center gap-2 mt-6">
                        <input 
                          type="checkbox" 
                          id={`enable_${pixel.id}`}
                          checked={pixels?.[pixel.id]?.enabled || false}
                          onChange={(e) => updatePixels({ 
                            ...pixels, 
                            [pixel.id]: { ...pixels[pixel.id], enabled: e.target.checked } 
                          })}
                          className="w-4 h-4 rounded border-gray-300 text-[#2E4E45] focus:ring-[#2E4E45]" 
                        />
                        <label htmlFor={`enable_${pixel.id}`} className="text-sm text-gray-600 whitespace-nowrap">تفعيل</label>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === "security" && (
                <div className="space-y-6">
                  <h3 className="text-base font-bold text-gray-800 mb-4">الحماية و Cloudflare</h3>
                  <p className="text-xs text-gray-400">إعدادات حماية الموقع والنماذج باستخدام Cloudflare Turnstile.</p>

                  <div className="flex items-center gap-4 p-4 rounded-lg border border-gray-100">
                    <div className="flex-1 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Turnstile Site Key (مفتاح الموقع)</label>
                        <input
                          type="text"
                          value={security?.turnstile?.siteKey || ""}
                          onChange={(e) => updateSecurity({ 
                            ...security, 
                            turnstile: { ...security.turnstile, siteKey: e.target.value } 
                          })}
                          placeholder="0x4AAAAAA..."
                          className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#2E4E45]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Turnstile Secret Key (المفتاح السري)</label>
                        <input
                          type="password"
                          value={security?.turnstile?.secretKey || ""}
                          onChange={(e) => updateSecurity({ 
                            ...security, 
                            turnstile: { ...security.turnstile, secretKey: e.target.value } 
                          })}
                          placeholder="0x4AAAAAA..."
                          className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#2E4E45]"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-6">
                      <input 
                        type="checkbox" 
                        id="enable_turnstile"
                        checked={security?.turnstile?.enabled || false}
                        onChange={(e) => updateSecurity({ 
                          ...security, 
                          turnstile: { ...security.turnstile, enabled: e.target.checked } 
                        })}
                        className="w-4 h-4 rounded border-gray-300 text-[#2E4E45] focus:ring-[#2E4E45]" 
                      />
                      <label htmlFor="enable_turnstile" className="text-sm text-gray-600 whitespace-nowrap">تفعيل الحماية</label>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "ai" && (
                <div className="space-y-6">
                  <h3 className="text-base font-bold text-gray-800 mb-4">إعدادات الذكاء الاصطناعي</h3>

                  <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 mb-4">
                    <div className="flex items-start gap-2">
                      <i className="ri-information-line text-violet-600 mt-0.5"></i>
                      <div>
                        <p className="text-sm font-semibold text-violet-800 mb-1">DNA Risk Score AI Report</p>
                        <p className="text-xs text-violet-700 leading-relaxed">
                          يتم إرسال وقراءة تقارير الـ DNA ومحاكاتها باستخدام نموذج الذكاء الاصطناعي المهيأ بأمان على خادم الباك اند.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">OpenAI API Key (إعداد آمن بالباك اند)</label>
                    <input
                      type="text"
                      disabled
                      value="تتم إدارة المفتاح بأمان في ملف .env على خادم الباك اند"
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-100 bg-gray-50 text-gray-500 text-sm focus:outline-none font-medium cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">نموذج الذكاء الاصطناعي</label>
                    <select
                      defaultValue="gpt-4o-mini"
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#2E4E45] focus:ring-2 focus:ring-[#2E4E45]/10"
                    >
                      <option value="gpt-4o-mini">GPT-4o Mini (موصى به — سريع واقتصادي)</option>
                      <option value="gpt-4o">GPT-4o (أعلى جودة — أغلى)</option>
                    </select>
                  </div>
                </div>
              )}

              {activeTab === "chatbot" && (
                <div className="space-y-6">
                  <h3 className="text-base font-bold text-gray-800 mb-4">إعدادات الشات بوت</h3>

                  <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                    <input type="checkbox" id="chatbot_enabled" defaultChecked className="w-4 h-4 rounded border-gray-300" />
                    <label htmlFor="chatbot_enabled" className="text-sm text-gray-700">
                      تفعيل الشات بوت على الموقع
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">الرسالة الترحيبية</label>
                    <textarea
                      rows={2}
                      defaultValue="مرحباً بك في مركزنا الطبي! كيف يمكنني مساعدتك اليوم؟"
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#2E4E45] resize-none"
                    />
                  </div>
                </div>
              )}

              {activeTab === "appearance" && (
                <div className="space-y-6">
                  <h3 className="text-base font-bold text-gray-800 mb-4">المظهر وحسابات التواصل الاجتماعي</h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">حسابات التواصل</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-gray-500 mb-1.5 block">X (Twitter)</label>
                        <input
                          type="text"
                          value={social.twitter || ""}
                          onChange={(e) => updateSocial({ ...social, twitter: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1.5 block">Instagram</label>
                        <input
                          type="text"
                          value={social.instagram || ""}
                          onChange={(e) => updateSocial({ ...social, instagram: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1.5 block">LinkedIn</label>
                        <input
                          type="text"
                          value={social.linkedin || ""}
                          onChange={(e) => updateSocial({ ...social, linkedin: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1.5 block">Snapchat</label>
                        <input
                          type="text"
                          value={social.snapchat || ""}
                          onChange={(e) => updateSocial({ ...social, snapchat: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">رابط خريطة قوقل (Map Embed URL)</label>
                    <input
                      type="text"
                      value={info.mapEmbedUrl || ""}
                      onChange={(e) => updateInfo({ ...info, mapEmbedUrl: e.target.value })}
                      placeholder="https://www.google.com/maps/embed?pb=..."
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">شعار العيادة (رابط الصورة)</label>
                    <input
                      type="text"
                      value={info.logo || ""}
                      onChange={(e) => updateInfo({ ...info, logo: e.target.value })}
                      placeholder="أدخل رابط صورة الشعار"
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none"
                    />
                  </div>
                </div>
              )}

              {activeTab === "terms" && (
                <div className="space-y-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2 border-b border-gray-100">
                    <div>
                      <h3 className="text-base font-bold text-gray-800">الشروط والأحكام وسياسة الخصوصية</h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        إدارة النصوص والبنود القانونية الكاملة باللغتين العربية والإنجليزية المخزنة في قاعدة البيانات.
                      </p>
                    </div>

                    <div className="flex items-center gap-1.5 p-1 bg-gray-100/80 rounded-xl self-start sm:self-auto">
                      <button
                        type="button"
                        onClick={() => setLegalSubTab("terms")}
                        className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                          legalSubTab === "terms"
                            ? "bg-white text-[#2E4E45] shadow-xs"
                            : "text-gray-500 hover:text-gray-800"
                        }`}
                      >
                        <i className="ri-file-list-3-line"></i>
                        <span>الشروط والأحكام</span>
                        <span className="text-[10px] bg-[#2E4E45]/10 text-[#2E4E45] px-1.5 py-0.2 rounded-full font-bold">
                          {(info.termsSections || []).length}
                        </span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setLegalSubTab("privacy")}
                        className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                          legalSubTab === "privacy"
                            ? "bg-white text-[#2E4E45] shadow-xs"
                            : "text-gray-500 hover:text-gray-800"
                        }`}
                      >
                        <i className="ri-shield-keyhole-line"></i>
                        <span>سياسة الخصوصية</span>
                        <span className="text-[10px] bg-[#2E4E45]/10 text-[#2E4E45] px-1.5 py-0.2 rounded-full font-bold">
                          {(info.privacySections || []).length}
                        </span>
                      </button>
                    </div>
                  </div>

                  {legalSubTab === "terms" && (
                    <div className="space-y-6 animate-fade-in">
                      {/* Summary Card */}
                      <div className="border border-gray-100 rounded-xl p-5 bg-gray-50/50 space-y-4">
                        <div className="flex items-center gap-2">
                          <i className="ri-information-line text-[#2E4E45] text-base"></i>
                          <h4 className="font-bold text-sm text-gray-800">
                            المقدمة والملخص العام للشروط (Summary / Overview)
                          </h4>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                              النص بالعربية
                            </label>
                            <textarea
                              rows={3}
                              value={info.terms || ""}
                              onChange={(e) => updateInfo({ ...info, terms: e.target.value })}
                              placeholder="أدخل ملخص الشروط والأحكام بالعربية..."
                              className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#2E4E45] bg-white resize-y"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                              النص بالإنجليزية (English)
                            </label>
                            <textarea
                              rows={3}
                              dir="ltr"
                              value={info.termsEn || ""}
                              onChange={(e) => updateInfo({ ...info, termsEn: e.target.value })}
                              placeholder="Enter terms and conditions summary in English..."
                              className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#2E4E45] bg-white resize-y"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Sections List */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <i className="ri-list-check-2 text-[#2E4E45] text-base"></i>
                            <h4 className="font-bold text-sm text-gray-800">
                              بنود الشروط والأحكام التفصيلية ({(info.termsSections || []).length} بند)
                            </h4>
                          </div>

                          <button
                            type="button"
                            onClick={handleAddTermsSection}
                            className="inline-flex items-center gap-1.5 text-xs font-bold text-[#2E4E45] bg-[#2E4E45]/10 hover:bg-[#2E4E45]/20 px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
                          >
                            <i className="ri-add-line text-sm"></i>
                            <span>إضافة بند جديد</span>
                          </button>
                        </div>

                        {(info.termsSections || []).length === 0 ? (
                          <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                            <p className="text-xs text-gray-400">لا توجد بنود مسجلة، انقر "إضافة بند جديد" لإضافة أول بند.</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {(info.termsSections || []).map((sec, idx) => (
                              <div
                                key={idx}
                                className="bg-white rounded-xl border border-gray-200 p-4 space-y-3 shadow-xs hover:border-gray-300 transition-colors"
                              >
                                <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                                  <div className="flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-md bg-[#2E4E45] text-white text-xs font-bold flex items-center justify-center">
                                      {idx + 1}
                                    </span>
                                    <span className="text-xs font-bold text-gray-700">
                                      {sec.title || `البند #${idx + 1}`}
                                    </span>
                                  </div>

                                  <button
                                    type="button"
                                    onClick={() => handleRemoveTermsSection(idx)}
                                    className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50 p-1.5 rounded-md transition-colors flex items-center gap-1 cursor-pointer"
                                    title="حذف هذا البند"
                                  >
                                    <i className="ri-delete-bin-line"></i>
                                    <span className="text-[11px]">حذف</span>
                                  </button>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                                      عنوان البند (بالعربية)
                                    </label>
                                    <input
                                      type="text"
                                      value={sec.title || ""}
                                      onChange={(e) => handleUpdateTermsSection(idx, "title", e.target.value)}
                                      placeholder="مثال: 1. قبول الشروط"
                                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs focus:outline-none focus:border-[#2E4E45]"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                                      Section Title (English)
                                    </label>
                                    <input
                                      type="text"
                                      dir="ltr"
                                      value={sec.title_en || ""}
                                      onChange={(e) => handleUpdateTermsSection(idx, "title_en", e.target.value)}
                                      placeholder="e.g. 1. Acceptance of Terms"
                                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs focus:outline-none focus:border-[#2E4E45]"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                                      تفاصيل ومحتوى البند (بالعربية)
                                    </label>
                                    <textarea
                                      rows={4}
                                      value={sec.content || ""}
                                      onChange={(e) => handleUpdateTermsSection(idx, "content", e.target.value)}
                                      placeholder="أدخل النص التفصيلي للبند بالعربية..."
                                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs focus:outline-none focus:border-[#2E4E45] resize-y"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                                      Section Content (English)
                                    </label>
                                    <textarea
                                      rows={4}
                                      dir="ltr"
                                      value={sec.content_en || ""}
                                      onChange={(e) => handleUpdateTermsSection(idx, "content_en", e.target.value)}
                                      placeholder="Enter detailed section content in English..."
                                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs focus:outline-none focus:border-[#2E4E45] resize-y"
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {legalSubTab === "privacy" && (
                    <div className="space-y-6 animate-fade-in">
                      {/* Privacy Summary Card */}
                      <div className="border border-gray-100 rounded-xl p-5 bg-gray-50/50 space-y-4">
                        <div className="flex items-center gap-2">
                          <i className="ri-information-line text-[#2E4E45] text-base"></i>
                          <h4 className="font-bold text-sm text-gray-800">
                            المقدمة والملخص العام للخصوصية (Privacy Overview)
                          </h4>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                              النص بالعربية
                            </label>
                            <textarea
                              rows={3}
                              value={info.privacy || ""}
                              onChange={(e) => updateInfo({ ...info, privacy: e.target.value })}
                              placeholder="أدخل ملخص سياسة الخصوصية بالعربية..."
                              className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#2E4E45] bg-white resize-y"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                              النص بالإنجليزية (English)
                            </label>
                            <textarea
                              rows={3}
                              dir="ltr"
                              value={info.privacyEn || ""}
                              onChange={(e) => updateInfo({ ...info, privacyEn: e.target.value })}
                              placeholder="Enter privacy policy summary in English..."
                              className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#2E4E45] bg-white resize-y"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Privacy Sections List */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <i className="ri-shield-check-line text-[#2E4E45] text-base"></i>
                            <h4 className="font-bold text-sm text-gray-800">
                              بنود سياسة الخصوصية التفصيلية ({(info.privacySections || []).length} بند)
                            </h4>
                          </div>

                          <button
                            type="button"
                            onClick={handleAddPrivacySection}
                            className="inline-flex items-center gap-1.5 text-xs font-bold text-[#2E4E45] bg-[#2E4E45]/10 hover:bg-[#2E4E45]/20 px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
                          >
                            <i className="ri-add-line text-sm"></i>
                            <span>إضافة بند جديد</span>
                          </button>
                        </div>

                        {(info.privacySections || []).length === 0 ? (
                          <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                            <p className="text-xs text-gray-400">لا توجد بنود مسجلة، انقر "إضافة بند جديد" لإضافة أول بند.</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {(info.privacySections || []).map((sec, idx) => (
                              <div
                                key={idx}
                                className="bg-white rounded-xl border border-gray-200 p-4 space-y-3 shadow-xs hover:border-gray-300 transition-colors"
                              >
                                <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                                  <div className="flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-md bg-[#2E4E45] text-white text-xs font-bold flex items-center justify-center">
                                      {idx + 1}
                                    </span>
                                    <span className="text-xs font-bold text-gray-700">
                                      {sec.title || `البند #${idx + 1}`}
                                    </span>
                                  </div>

                                  <button
                                    type="button"
                                    onClick={() => handleRemovePrivacySection(idx)}
                                    className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50 p-1.5 rounded-md transition-colors flex items-center gap-1 cursor-pointer"
                                    title="حذف هذا البند"
                                  >
                                    <i className="ri-delete-bin-line"></i>
                                    <span className="text-[11px]">حذف</span>
                                  </button>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                                      عنوان البند (بالعربية)
                                    </label>
                                    <input
                                      type="text"
                                      value={sec.title || ""}
                                      onChange={(e) => handleUpdatePrivacySection(idx, "title", e.target.value)}
                                      placeholder="مثال: 1. مقدمة"
                                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs focus:outline-none focus:border-[#2E4E45]"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                                      Section Title (English)
                                    </label>
                                    <input
                                      type="text"
                                      dir="ltr"
                                      value={sec.title_en || ""}
                                      onChange={(e) => handleUpdatePrivacySection(idx, "title_en", e.target.value)}
                                      placeholder="e.g. 1. Introduction"
                                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs focus:outline-none focus:border-[#2E4E45]"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                                      تفاصيل ومحتوى البند (بالعربية)
                                    </label>
                                    <textarea
                                      rows={4}
                                      value={sec.content || ""}
                                      onChange={(e) => handleUpdatePrivacySection(idx, "content", e.target.value)}
                                      placeholder="أدخل النص التفصيلي للبند بالعربية..."
                                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs focus:outline-none focus:border-[#2E4E45] resize-y"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                                      Section Content (English)
                                    </label>
                                    <textarea
                                      rows={4}
                                      dir="ltr"
                                      value={sec.content_en || ""}
                                      onChange={(e) => handleUpdatePrivacySection(idx, "content_en", e.target.value)}
                                      placeholder="Enter detailed section content in English..."
                                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs focus:outline-none focus:border-[#2E4E45] resize-y"
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Save Button */}
              <div className="pt-4 border-t border-gray-100 flex justify-end gap-3">
                {hasChanges && (
                  <button
                    onClick={reset}
                    className="px-5 py-2.5 rounded-lg border border-gray-200 text-gray-500 text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    تراجع
                  </button>
                )}
                <button
                  onClick={handleSave}
                  disabled={!hasChanges && saveStatus !== "error"}
                  className={`px-6 py-2.5 rounded-lg text-white text-sm font-medium transition-all flex items-center gap-2 cursor-pointer ${
                    hasChanges
                      ? "bg-[#2E4E45] hover:bg-[#243d36]"
                      : "bg-gray-300 cursor-not-allowed"
                  }`}
                >
                  <i className="ri-save-line" />
                  حفظ الإعدادات
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}